
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CoverPage } from "./components/CoverPage";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminContact from "./pages/AdminContact";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";

// Protected route component for admin routes
const AdminProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAdminLoggedIn = sessionStorage.getItem("isAdminLoggedIn") === "true";
  
  if (!isAdminLoggedIn) {
    // Redirect to login if not authenticated as admin
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Protected route component for authenticated users
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      setIsAuthenticated(!!data.session);
    };
    
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setIsAuthenticated(!!session);
      }
    );
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  // Show loading while checking auth
  if (isAuthenticated === null) {
    return <div>Chargement...</div>;
  }
  
  if (!isAuthenticated) {
    // Redirect to auth if not authenticated
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

// Create a new QueryClient instance
const queryClient = new QueryClient();

const App = () => {
  const [showCover, setShowCover] = useState(true);
  
  // Check if the cover has already been shown in this session
  useEffect(() => {
    const coverShown = sessionStorage.getItem('coverShown');
    if (coverShown) {
      setShowCover(false);
    }
  }, []);
  
  const handleCoverComplete = () => {
    setShowCover(false);
    // Save to session storage so it doesn't show again during this session
    sessionStorage.setItem('coverShown', 'true');
  };

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          
          {showCover && <CoverPage onComplete={handleCoverComplete} />}
          
          {/* Ajout de la classe pour animer l'entrée du contenu principal */}
          <div className={`transition-opacity duration-500 ${showCover ? 'opacity-0' : 'opacity-100'}`}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route 
                path="/admin" 
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                } 
              />
              <Route path="/admin-contact" element={<AdminContact />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </TooltipProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default App;
